/* eslint-disable react/no-array-index-key */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NextButton, PrevButton } from "@ucs/Components/EmblaCarousel/EmblaCarouselButtons";
import UCSColors from "@ucs/Utils/UCSColors";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface EmblaProps {
    id: any;
    images: EmblaImage[];
    extendedStyles?: CarouselStyles;
}

export interface EmblaImage {
    name?: string;
    url: string;
    altText?: string;
}

export interface CarouselStyles {
    wrapper?: InjectableCss;
    imageStyles?: InjectableCss;
    mainContainer?: InjectableCss;
    carouselItem?: InjectableCss;
    button?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    captions?: InjectableCss;
    dotButton?: ButtonPresentationProps;
}

export const carouselStyles: CarouselStyles = {
    wrapper: {
        css: css`
            position: relative;
            width: 100%;
            /* max-height: 720px; */
            height: 100%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-height: 400px;
                    `,
                    css`
                        min-height: 520px;
                    `,
                ]);
            }}
        `,
    },
    mainContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
            height: 100%;
            background-color: black;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        min-height: 400px;
                    `,
                    css`
                        min-height: 520px;
                    `,
                ]);
            }}
        `,
    },
    carouselItem: {
        css: css`
            width: 100%;
        `,
    },
    button: {
        hoverStyle: { backgroundColor: "transparent" },
        activeStyle: { backgroundColor: "transparent" },
        css: css`
            cursor: pointer;
            background-color: transparent;
            touch-action: manipulation;
            position: absolute;
            z-index: 1;
            left: 0;
            top: 50%;
            transform: translateY(-50%);
            border: 0;
            width: 30px;
            height: 30px;
            justify-content: center;
            align-items: center;
            padding: 0;
            &:disabled {
                background-color: transparent;
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            width: fit-content;
            display: flex;
            justify-content: center;
            position: absolute;
            left: 50%;
            bottom: 15px;
            transform: translate(-50%, 0);
            background: rgba(0, 0, 0, 0.5);
            border-radius: 20px;
            padding: 4px 6px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        bottom: 8px;
                    `,
                    css`
                        bottom: 8px;
                    `,
                ]);
            }}
        `,
    },
    captions: {
        css: css`
            width: auto;
            display: flex;
            justify-content: center;
            position: absolute;
            left: 50%;
            bottom: 45px;
            transform: translate(-50%, 0);
            color: ${UCSColors.gray03};
            background: rgba(255, 255, 255, 0.75);
            border-radius: 20px;
            padding: 1px 10px 2px;
            font-size: 14px;
            line-height: 18px;
            text-align: center;
            max-width: 94%;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        bottom: 30px;
                        font-size: 12px;
                        line-height: 14px;
                        padding: 1px 8px;
                    `,
                    css`
                        bottom: 30px;
                        font-size: 12px;
                        line-height: 14px;
                        padding: 1px 8px;
                    `,
                ]);
            }}
        `,
    },
    dotButton: {
        css: css`
            min-height: 8px !important;
            height: 8px;
            width: 8px;
            border-radius: 4px;
            padding: 0;
            margin: 0 6px;
            background: rgba(0, 0, 0, 0.3);
            border: 1px solid #fff;
            &:hover,
            &:active {
                background: rgba(230, 230, 230, 0.3);
                border: 1px solid #fff;
            }
            &:focus {
                outline: none;
            }
        `,
    },
};

const CarouselSlidesContainer = styled.div`
    display: flex;
    height: 100%;
    width: 100%;
    ${({ theme }) => {
        return breakpointMediaQueries(theme, [
            css`
                min-height: 400px;
            `,
            css`
                min-height: 520px;
            `,
        ]);
    }}
`;

const CarouselSlide = styled.div`
    padding: 0;
    position: relative;
    flex: 0 0 auto;
    width: 100%;
`;

const CarouselSlideInner = styled.div`
    width: 100%;
    padding: 0;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const ImageDiv = styled("div")<{
    image: string;
}>`
    width: 100vw;
    height: 100%;
    /* max-height: 720px; */
    background-image: url("${p => p.image}");
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    ${injectCss}
`;

const EmblaSlideShow = ({ id, images, extendedStyles }: EmblaProps) => {
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    const rightButtonStyles: ButtonPresentationProps = {
        hoverStyle: styles.button?.hoverStyle,
        activeStyle: styles.button?.activeStyle,
        css: css`
            ${styles.button?.css}
            left: unset;
            right: 0;
            transform: translate(1px, -50%);
        `,
    };

    const [emblaRef, embla] = useEmblaCarousel({
        align: "start",
        loop: true,
    });

    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
    const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

    const onSelect = useCallback(() => {
        if (!embla) {
            return;
        }
        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
    }, [embla]);

    useEffect(() => {
        if (!embla || !images || images.length === 0) {
            return;
        }
        embla.reInit({
            align: "start",
            loop: true,
        });
        setSelectedIndex(0);
        embla.scrollTo(0);
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
    }, [embla, images]);

    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.on("select", onSelect);
        onSelect();
    }, [embla, onSelect]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.mainContainer} ref={emblaRef}>
                <CarouselSlidesContainer>
                    {images.map((image, index) =>
                        !image || image.url === "" ? null : (
                            <CarouselSlide key={index}>
                                <CarouselSlideInner>
                                    {/* <ImageDiv image={image.url} {...styles.imageStyles}></ImageDiv> */}
                                    <LazyImage src={image.url} altText=""></LazyImage>
                                </CarouselSlideInner>
                            </CarouselSlide>
                        ),
                    )}
                </CarouselSlidesContainer>
            </StyledWrapper>
            {images.length > 1 && (
                <StyledWrapper {...styles.dotsContainerWrapper}>
                    {images.map((_, i) => {
                        const dotColorStyles =
                            i === selectedIndex
                                ? `
                                    background: white;
                                    &:hover { background: white; }
                                `
                                : "";
                        const dotButtonStyles = {
                            css: css`
                                ${styles.dotButton?.css || ""}
                                ${dotColorStyles}
                            `,
                        };
                        // eslint-disable-next-line react/no-array-index-key
                        return <Button key={i} {...dotButtonStyles} onClick={() => embla && embla.scrollTo(i)} />;
                    })}
                </StyledWrapper>
            )}
            {images.length > 0 &&
                images.map((image, i) => {
                    return image.altText && i === selectedIndex ? (
                        <Typography {...styles.captions}>{image.altText}</Typography>
                    ) : null;
                })}
            {images.length > 1 && (
                <>
                    <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} styles={styles.button!} />
                    <NextButton onClick={scrollNext} enabled={nextBtnEnabled} styles={rightButtonStyles} />
                </>
            )}
        </StyledWrapper>
    );
};

export default EmblaSlideShow;
